import * as React from 'react'
import { Redirect, useHistory, useLocation } from 'react-router-dom'

import { EscapeRoomDetailsModal } from '../../components/molecules/EscapeRoomDetailsModal'
import { EditEscapeRoomStepOne } from '../../components/organisms/EditEscapeRoomStepOne'
import { EditEscapeRoomStepTwo } from '../../components/organisms/EditEscapeRoomStepTwo'
import { createEscapeRoom, getEscapeRoom, updateEscapeRoom } from '../../services/escape-room-services'
import { getPuzzles } from '../../services/puzzles-services'
import { invoke, useInvokeEffect } from '../../utils/helper'
import { FileType, PuzzleDto, PuzzleHeaderDto } from '../../utils/types'

interface Props {
    isCreate?: boolean
}

const playerCount = 5

export const EditEscapeRoom: React.FC<Props> = ({ isCreate }) => {
    const [title, setTitle] = React.useState('')
    const [description, setDescription] = React.useState('')
    const [descriptionImage, setDescriptionImage] = React.useState<FileType | undefined>()
    const [bannerImage, setBannerImage] = React.useState<FileType | undefined>()
    const [puzzles, setPuzzles] = React.useState<PuzzleHeaderDto[]>([])
    const [step, setStep] = React.useState<1|2|3|4>(1)
    const [puzzleSlots, setPuzzleSlots] = React.useState<PuzzleHeaderDto[]>([])

    const { state }: { state: { escapeRoomId: string } | undefined } = useLocation()
    const escapeRoomId = state?.escapeRoomId

    const history = useHistory()

    useInvokeEffect(async () => {

        if (escapeRoomId) {
            const escapeRoom = await getEscapeRoom(escapeRoomId)
            setTitle(escapeRoom.title)
            setDescription(escapeRoom.description)
            setDescriptionImage(escapeRoom.descriptionImageUri ? {
                name: 'description',
                key: 'description',
                uri: escapeRoom.descriptionImageUri
            } : undefined)
            setBannerImage(escapeRoom.bannerImageUri ? {
                name: 'banner',
                key: 'banner',
                uri: escapeRoom.bannerImageUri
            } : undefined)
            setPuzzleSlots(escapeRoom.puzzles)
        }

        setPuzzles(
            await getPuzzles()
        )
    }, [], {
        operation: 'Liste Opgaver',
        errorFunc: () => { history.push('/') }
    })

    if (!isCreate && !escapeRoomId) {
        return <Redirect to="/"/>
    }

    const update = async () => {
        setStep(4)

        const updateRequest = {
            playerCount,
            title,
            description,
            bannerImageUri: bannerImage?.uri,
            descriptionImageUri: descriptionImage?.uri,
            materials: [],
            puzzles: puzzleSlots.map((pz) => {
                return {
                    seconds: pz!!.seconds,
                    puzzleId: pz!!.puzzleId
                }
            })
        }

        await invoke(async () => {
            if (escapeRoomId) {
                await updateEscapeRoom(escapeRoomId, updateRequest)
            } else {
                await createEscapeRoom(updateRequest)
            }

            history.push('/')
        }, {
            operation: 'Gemmer Escape Room',
            showSuccess: true
        })
    }

    return (
        <div>
            {
                step === 1 && (
                    <EditEscapeRoomStepOne
                        description={description}
                        title={title}
                        descriptionImage={descriptionImage}
                        bannerImage={bannerImage}
                        onChangeDescription={setDescription}
                        onChangeDescriptionImage={setDescriptionImage}
                        onChangeBannerImage={setBannerImage}
                        onChangeTitle={setTitle}
                        onNext={() => setStep(2)}/>
                )
            }
            {
                step === 2 && (
                    <EditEscapeRoomStepTwo
                        minSlots={2}
                        maxSlots={13}
                        availablePuzzles={puzzles}
                        puzzleSlots={puzzleSlots}
                        onChangePuzzleSlots={setPuzzleSlots}
                        onContinue={() => setStep(3)}/>
                )
            }
            {
                step === 3 && (
                    <EscapeRoomDetailsModal
                        descriptionImage={descriptionImage?.uri}
                        bannerImage={bannerImage?.uri}
                        description={description}
                        open={step === 3}
                        onClose={() => setStep(2)}
                        title={title}
                        continueButtonTitle={escapeRoomId ? 'Gem Escape Room' : 'Opret dette escape room'}
                        onContinue={update}
                        puzzles={puzzleSlots.filter((pz) => pz) as PuzzleDto[]}/>
                )
            }
        </div>

    )
}

EditEscapeRoom.defaultProps = {
    isCreate: false
}
